import { authorizedAPI } from "@/config/axios.config";
import { UtilsService } from "../utils";
import type { CreateDelegationInput } from "@/types";

const utils = new UtilsService();

/**
 * The reviewer's side of a delegation — creating one and deciding on what comes
 * back. Distinct from the token-based `delegationService`, which is the
 * recipient's unauthenticated side.
 */
class OwnerDelegationService {
  // POST /form-delegations
  create(input: CreateDelegationInput): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post("/form-delegations", input)
    );
  }

  // GET /form-delegations/{applicantProcessId}/{formId}
  getForForm(applicantProcessId: string, formId: string): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/form-delegations/${applicantProcessId}/${formId}`)
    );
  }

  // POST /form-delegations/{id}/approve
  approve(
    id: string,
    body: { comment?: string; responses?: unknown; nextStaffId?: string }
  ): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/form-delegations/${id}/approve`, body)
    );
  }

  // POST /form-delegations/{id}/request-changes
  requestChanges(id: string, comment: string): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/form-delegations/${id}/request-changes`, { comment })
    );
  }

  // POST /form-delegations/{id}/resend/{recipientId}
  resend(id: string, recipientId: string): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/form-delegations/${id}/resend/${recipientId}`)
    );
  }

  // DELETE /form-delegations/{id}
  cancel(id: string, reason?: string): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.delete(`/form-delegations/${id}`, { data: { reason } })
    );
  }

  // GET /form-delegations/assigned-to-me
  assignedToMe(): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get("/form-delegations/assigned-to-me")
    );
  }

  // PATCH /form-delegations/{id}/recipients
  updateRecipients(
    id: string,
    recipients: { email: string; name?: string; role: "EDITOR" | "VIEWER" }[]
  ): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.patch(`/form-delegations/${id}/recipients`, { recipients })
    );
  }

  // GET /form-delegations/mine/{id}
  viewMine(id: string): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.get(`/form-delegations/mine/${id}`)
    );
  }

  // POST /form-delegations/mine/{id}/save
  saveMine(id: string, responses: unknown): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/form-delegations/mine/${id}/save`, { responses })
    );
  }

  // POST /form-delegations/mine/{id}/submit
  submitMine(id: string, responses: unknown): Promise<any> {
    return utils.handleApiRequest(() =>
      authorizedAPI.post(`/form-delegations/mine/${id}/submit`, { responses })
    );
  }
}

export const ownerDelegationService = new OwnerDelegationService();
